import React, { useCallback, useRef, useState } from 'react'
import { useUpdateEffect } from 'react-use'
import { Navigation, Pagination, Swiper as SwiperType, Thumbs, Zoom } from 'swiper'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'

import { useIsBrowser, useSupportsHover } from '@syconium/little-miss-figgy'

import { useFeatureFlag } from '../../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import { GalleryImageStateContainer } from '../../../components/pages/products/ProductPage/contexts/GalleryImageStateContainer'
import { heapTrack } from '../../../lib/analytics'

import { IProps, ProductAsset } from './IProps'
import { NavigationArrows } from './NavigationArrows'
import * as Styled from './styles'
import { ThumbNailsDisplay } from './ThumbNailsDisplay'

const swiperModules = [Navigation, Pagination, Thumbs, Zoom]

export const SwiperImageSlider: React.FC<IProps> = ({
  fitModelCaption,
  iconBadge,
  iconBadgePlacement,
  isKit = false,
  shouldDisplayIconBadge,
  showExtraLargeIcon,
  showImageZoom,
  sliderAssets,
  ...rest
}) => {
  const {
    magnolia: {
      general: { playVideo, pauseVideo, muteVideo, unmuteVideo },
    },
  } = useTranslation()
  const isVideo = (index: number) => {
    return sliderAssets[index]?.type === 'ProductVideo'
  }
  const swiperPaginationConfig = {
    renderBullet: (index: number) => {
      return `<div class="swiper-pagination-bullet ${
        isVideo(index) ? 'swiper-pagination-video-bullet' : ''
      }"></div>`
    },
    clickable: true,
  }

  const aspectRatioFeatureFlag = useFeatureFlag<'aspect-ratio-control' | 'aspect-ratio-variant'>({
    key: 'aspect-ratio',
    ifAccessedPriorToDecisionInitialization: 'lock-pageview-to-controlVariant',
    controlVariant: 'aspect-ratio-control',
  })
  const isAspectRatioVariant = aspectRatioFeatureFlag === 'aspect-ratio-variant'
  const swiperRef = useRef<SwiperType>()
  const swiperRefInit = useRef<SwiperRef>(null)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType>()

  const { currentAssetIndex, setCurrentImageIndex, isZoomed, isImageZoomShown } =
    GalleryImageStateContainer.useContainer()

  const isBrowser = useIsBrowser()

  // It is OK to use JavaScript device checking here
  // since the feature we will be toggling (pagination)
  // will not cause any CLS if it changes between the server
  // and the client. Don't use this variable for other
  // visual changes in our rendered output.
  const hoverSupported = useSupportsHover()

  useUpdateEffect(() => {
    if (!isImageZoomShown) {
      if (swiperRefInit.current && swiperRefInit?.current.swiper) {
        const { swiper } = swiperRefInit.current
        swiper.slideToLoop(currentAssetIndex, 0)
      }
    }
  }, [isImageZoomShown, isZoomed])

  const onSwiperSwipe = (direction: string) => {
    const directionLabel = direction == 'prev' ? 'previous' : 'next'
    heapTrack('mobile-image-swipe', { category: 'gallery', action: `view ${directionLabel} image` })
  }

  const onSwiperSlideChangeCallback = useCallback(
    (swiper: SwiperType) => {
      const isSwiped = !!swiper.swipeDirection
      if (isSwiped) onSwiperSwipe(swiper.swipeDirection)

      setCurrentImageIndex(swiper.activeIndex)
    },
    [setCurrentImageIndex]
  )
  const onBeforeSwiperInit = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper
  }, [])
  return (
    <Styled.RootWrapper>
      <Styled.HideIfShortOnSpace>
        <ThumbNailsDisplay
          isKit={isKit}
          onThumbsSwiperInit={setThumbsSwiper}
          sliderAssets={sliderAssets}
          gallerySwiperRef={swiperRef}
        />
      </Styled.HideIfShortOnSpace>
      <Styled.GallerySliderWrapper>
        <Swiper
          loop
          modules={swiperModules}
          onBeforeInit={onBeforeSwiperInit}
          pagination={
            sliderAssets.length > 1 && hoverSupported === false ? swiperPaginationConfig : false
          }
          ref={swiperRefInit}
          slidesPerView={1}
          spaceBetween={0}
          speed={400}
          thumbs={{ swiper: thumbsSwiper }}
          zoom={true}
          onSlideChange={onSwiperSlideChangeCallback}
          {...rest}
        >
          {sliderAssets.map((slide: ProductAsset, index) => {
            // Only render one image for serverside render since this 3rd
            // party swiper does not support SSR... cry a little
            if (!isBrowser && index !== currentAssetIndex) return null

            if (slide.type === 'ProductImage') {
              return (
                <SwiperSlide
                  key={slide.source}
                  onClick={() => showImageZoom && showImageZoom(swiperRef.current?.realIndex)}
                >
                  <Styled.GalleryImage
                    src={slide.source}
                    // @TODO: caption & name are always undefined here, but we should still
                    // support the ability for these values to be surfaced for the alt attribute
                    // if present.
                    alt={slide.caption ?? slide.name ?? ''}
                    fetchPriority={index === 0 ? 'high' : undefined}
                    loading='default'
                    widths={{ unit: 'vw', sm: 100, md: 66 }}
                    aspectRatios={{
                      sm: !isAspectRatioVariant ? 1 : 0.768,
                      md: !isAspectRatioVariant ? 1 : 0.768,
                    }}
                  />
                </SwiperSlide>
              )
            }
            if (slide.type === 'ProductVideo') {
              if (slide.kind === 'catwalk') {
                return (
                  <SwiperSlide key={slide.source}>
                    <Styled.GalleryVideo
                      autoPlay
                      muted
                      loop
                      playsInline
                      src={slide.source}
                      type={'video/mp4'}
                      displayPauseButton={true}
                      aspectRatios={{
                        sm: isAspectRatioVariant ? 0.768 : 1,
                        md: isAspectRatioVariant ? 0.768 : 1,
                      }}
                      screenReaderStrings={{
                        pauseText: pauseVideo,
                        playText: playVideo,
                        muteText: muteVideo,
                        unMuteText: unmuteVideo,
                      }}
                      onClick={event => {
                        if (event.target === event.currentTarget) {
                          showImageZoom && showImageZoom(swiperRef.current?.realIndex)
                        }
                      }}
                    />
                  </SwiperSlide>
                )
              }

              if (slide.kind === 'ugc') {
                return (
                  <SwiperSlide key={slide.source}>
                    <Styled.PDPAudioVideo
                      autoPlay
                      loop
                      muted
                      playsInline
                      src={slide.source}
                      type={'video/mp4'}
                      displayPauseButton={true}
                      displayMuteButton={true}
                      aspectRatios={{
                        sm: isAspectRatioVariant ? 0.768 : 1,
                        md: isAspectRatioVariant ? 0.768 : 1,
                      }}
                      screenReaderStrings={{
                        pauseText: pauseVideo,
                        playText: playVideo,
                        muteText: muteVideo,
                        unMuteText: unmuteVideo,
                      }}
                      onClick={event => {
                        if (event.target === event.currentTarget) {
                          showImageZoom && showImageZoom(swiperRef.current?.realIndex)
                        }
                      }}
                    />
                  </SwiperSlide>
                )
              }
            }
            return <SwiperSlide key={slide.source} />
          })}
          {hoverSupported === true ? <NavigationArrows gallerySwiperRef={swiperRef} /> : null}
        </Swiper>
        {fitModelCaption ? fitModelCaption : null}
      </Styled.GallerySliderWrapper>
      {iconBadge && (
        <Styled.IconBadge
          {...iconBadge}
          iconBadgePlacement={iconBadgePlacement}
          shouldDisplay={shouldDisplayIconBadge}
          size={showExtraLargeIcon ? 'extraLarge' : 'large'}
        />
      )}
    </Styled.RootWrapper>
  )
}
